import Quill from "quill";
import { firstValueFrom } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { FileUploadWithPreview } from "file-upload-with-preview";
import { Blog, ImageType } from "src/app/shared/types/blog-types";
import { BlogService } from "src/app/shared/services/blog.service";
import { PicklistType } from "src/app/shared/enums/picklist-types";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { UploadService } from "src/app/shared/services/upload-file.service";
import { Component, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { PicklistSelectComponent } from "../../picklist/picklist-select/picklist-select.component";

@Component({
  templateUrl: "./blog-form.component.html",
  standalone: false,
})
export class BlogFormComponent implements OnInit {
  public form!: FormGroup;
  public formSelectorCategory!: FormGroup;
  public formSelectorLanguage!: FormGroup;
  public formSelectorApplication!: FormGroup;
  public blogId?: number;

  public imageUpload!: FileUploadWithPreview;
  public PicklistType = PicklistType;
  selectedType!: string;
  isLoading = false;
  loading = false;
  quillEditor!: Quill;
  @ViewChild("categorySelect") categorySelect!: PicklistSelectComponent;
  @ViewChild("lanSelect") lanSelect!: PicklistSelectComponent;
  @ViewChild("appSelect") appSelect!: PicklistSelectComponent;
  @ViewChild("addCategoryTemplate") categoryModalTemplate!: TemplateRef<unknown>;

  addCategoryModal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(private router: Router, private fb: FormBuilder, private route: ActivatedRoute, private blogService: BlogService, private translate: TranslateService, private uploadService: UploadService) {
    this.form = this.fb.group({
      id: [0],
      title: ["", Validators.required],
      content: ["", Validators.required],
      categoryId: [0, Validators.required],
      applicationId: [0, Validators.required],
      languageId: [0, Validators.required],
      created: [new Date()],
      updatedAt: [new Date()],
      images: [[]],
      isTutorial: [false],
    });

    this.formSelectorCategory = this.fb.group({
      id: [0],
      title: ["", Validators.required],
      description: [""],
      delimeter: [""],
      value: [""],
      createdAt: [new Date()],
      updatedAt: [new Date()],
    });
    this.formSelectorLanguage = this.fb.group({
      id: [0],
      title: ["", Validators.required],
      description: [""],
      delimeter: [""],
      value: [""],
      createdAt: [new Date()],
      updatedAt: [new Date()],
    });
    this.formSelectorApplication = this.fb.group({
      id: [0],
      title: ["", Validators.required],
      description: [""],
      delimeter: [""],
      value: [""],
      createdAt: [new Date()],
      updatedAt: [new Date()],
    });
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const idParam = params["id"];

      if (idParam) {
        this.blogId = idParam;
        this.loading = true;
        const loadSubscription = this.blogService.getBlog(idParam).subscribe({
          next: (response: Blog) => {
            loadSubscription.unsubscribe();
            this.form.patchValue(response);
            this.formSelectorCategory.patchValue(response.category);
            this.formSelectorApplication.patchValue(response.application);
            this.formSelectorLanguage.patchValue(response.language);
            this.initializeImageUpload();
            this.loading = false;
          },
          error: () => {
            loadSubscription.unsubscribe();
            showMessage(PopupType.Danger, this.translate.instant("blog_load_error"));
          },
        });
      }
      else {
        this.initializeImageUpload();
      }
    });
  }

  initializeImageUpload() {
    this.translate.get(["click_to_upload_files", "choose_files", "your_files"]).subscribe((translations) => {
      setTimeout(() => {
        this.imageUpload = new FileUploadWithPreview("blog-images", {
          maxFileCount: 1,
          accept: "image/*",
          multiple: true,
          images: {
            baseImage: this.ExtractImage(this.form.value.images),
          },
          text: {
            browse: translations["click_to_upload_files"],
            chooseFile: translations["choose_files"],
            label: translations["your_files"],
          },
          showDeleteButtonOnImages: true,
        });
      });
    });
  }

  ExtractImage(images?: ImageType[]) {
    if (images && images.length > 0) {
      let imageUrl = images[0].url;
      if (imageUrl.startsWith("zephyr/")) {
        imageUrl = imageUrl.substring(7);
      }
      return imageUrl;
    }
    return "/assets/images/custom-image.svg";
  }

  handleSave() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.form.value.categoryId = this.formSelectorCategory.value.id;
    this.form.value.languageId = this.formSelectorLanguage.value.id;
    this.form.value.applicationId = this.formSelectorApplication.value.id;

    if (this.formSelectorCategory.value.id) {
      this.isLoading = true;

      const blogData = {
        ...this.form.value,
        updatedAt: new Date(),
      };

      const files = Array.from(this.imageUpload.cachedFileArray).map((file: File) => {
        const newFileName = file.name.replace(/:upload:[a-zA-Z0-9]+$/, "");
        const newFile = new File([file], newFileName, {
          type: file.type,
          lastModified: file.lastModified,
        });
        return newFile;
      });
      if (this.blogId) {
        const updateBlogSubscription = this.blogService.updateBlog(this.blogId, blogData, files).subscribe({
          next: () => {
            this.isLoading = false;
            updateBlogSubscription.unsubscribe();
            showMessage(PopupType.Success, this.translate.instant("blog_updated"));
            this.router.navigate(["/blogs"]);
          },
          error: (error) => {
            updateBlogSubscription.unsubscribe();
            this.isLoading = false;
            showMessage(PopupType.Danger, this.translate.instant(error.error.message));
          },
        });
      }
      else {
        const addSubscription = this.blogService.addBlog(blogData, files).subscribe({
          next: () => {
            addSubscription.unsubscribe();
            this.isLoading = false;
            showMessage(PopupType.Success, this.translate.instant("blog_added"));
            this.router.navigate(["/blogs"]);
          },
          error: (error) => {
            addSubscription.unsubscribe();
            this.isLoading = false;
            showMessage(PopupType.Danger, error.error.message ?? error.error);
          },
        });
      }
    }
    else {
      showMessage(PopupType.Danger, this.translate.instant("invalid_blog_added"));
    }
  }

  categoryAdded(successful: boolean) {
    if (successful) {
      this.addCategoryModal.close();
      this.categorySelect.fetchOptions();
      this.lanSelect.fetchOptions();
      this.appSelect.fetchOptions();
    }
  }

  handleDelete(id: number) {
    this.isLoading = true;
    const deleteSubscription = this.blogService.deleteBlog(id).subscribe({
      next: () => {
        deleteSubscription.unsubscribe();
        this.isLoading = false;
        showMessage(PopupType.Success, this.translate.instant("blog_deleted"));
        this.router.navigate(["/blogs"]);
      },
      error: () => {
        deleteSubscription.unsubscribe();
        this.isLoading = false;
        showMessage(PopupType.Danger, this.translate.instant("invalid_blog_deleted"));
      },
    });
  }

  handleRoute(path: string) {
    this.router.navigate([path]);
  }

  addModal(type: string) {
    this.selectedType = type;
    this.addCategoryModal = this.dialog.open(this.categoryModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  modules1 = {
    toolbar: {
      container: [
        ["bold", "italic", "underline", "strike"],
        ["blockquote", "code-block"],
        [{ header: 1 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ direction: "rtl" }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ["clean"],
        ["link", "image"],
      ],
      handlers: {
        image: () => this.customImageHandler(),
      },
    },
  };

  customImageHandler() {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          const uploadResponse = await firstValueFrom(this.uploadService.uploadFile(file));
          const imageUrl = uploadResponse.imageUrl;

          const range = this.quillEditor.getSelection(true);
          this.quillEditor.insertEmbed(range.index, "image", imageUrl, "user");
        }
        catch (err: any) {
          console.error("Image upload failed", err);
          showMessage(PopupType.Danger, err.error.message ?? err.error);
        }
      }
    };
  }

  onEditorCreated(quill: any) {
    this.quillEditor = quill;
  }
}
